import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { Node, Edge, EdgeChange } from "@xyflow/react";
import type { MergeEntry } from "./types";
import { CommonAncestorValidator } from "./common-ancestor-validator";

type WorkflowEdge = Edge & {
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
};

export class ConnectionManager {
  private static getStore() {
    return useWorkflowStore.getState();
  }

  static async createConnectionsToMerge(
    mergeNodeId: string,
    entries: MergeEntry[],
  ): Promise<void> {
    const store = this.getStore();
    const { nodes, edges, onEdgesChange } = store;

    const newEdges: Edge[] = [];
    const edgesToRemove: string[] = [];

    const mergeNode = nodes.find((n) => n.id === mergeNodeId);
    if (!mergeNode) {
      return;
    }

    entries.forEach((entry, index) => {
      const entryNodeId = entry.nodeId;

      if (!entryNodeId) {
        return;
      }

      const sourceNode = nodes.find((n) => n.id === entryNodeId);
      if (!sourceNode) {
        return;
      }

      const existingEdges = edges.filter(
        (edge) =>
          edge.source === entryNodeId &&
          edge.target !== mergeNodeId &&
          nodes.find((n) => n.id === edge.target)?.data?.type === "merge-node",
      );

      edgesToRemove.push(...existingEdges.map((edge) => edge.id));

      const newEdge: Edge = {
        id: `${entryNodeId}-${mergeNodeId}-${index}`,
        source: entryNodeId,
        target: mergeNodeId,
        sourceHandle: "source",
        targetHandle: `merge-input-${index + 1}`,
        type: "default",
        animated: false,
        data: {
          label: entry.name || `Entrada ${index + 1}`,
        },
      };

      newEdges.push(newEdge);
    });

    const edgeChanges: EdgeChange[] = [];

    const existingMergeEdges = edges.filter(
      (edge) => edge.target === mergeNodeId,
    );

    existingMergeEdges.forEach((edge) => {
      edgeChanges.push({
        type: "remove",
        id: edge.id,
      });
    });

    newEdges.forEach((edge) => {
      edgeChanges.push({
        type: "add",
        item: edge,
      });
    });

    onEdgesChange(edgeChanges);
  }

  static async removeMergeConnections(mergeNodeId: string): Promise<void> {
    const store = this.getStore();
    const { edges, onEdgesChange } = store;

    const edgesToRemove = edges.filter(
      (edge: WorkflowEdge) => edge.target === mergeNodeId,
    );

    const edgeChanges: EdgeChange[] = edgesToRemove.map((edge) => ({
      type: "remove" as const,
      id: edge.id,
    }));

    onEdgesChange(edgeChanges);
  }

  static async updateConnections(
    mergeNodeId: string,
    oldEntries: MergeEntry[],
    newEntries: MergeEntry[],
  ): Promise<void> {
    const store = this.getStore();
    const { edges, onEdgesChange } = store;

    const edgeChanges: EdgeChange[] = [];
    const oldNodeIds = new Set(oldEntries.map((e) => e.nodeId));
    const newNodeIds = new Set(newEntries.map((e) => e.nodeId));

    const removedNodeIds = Array.from(oldNodeIds).filter(
      (id) => !newNodeIds.has(id),
    );
    removedNodeIds.forEach((nodeId) => {
      const edgeToRemove = edges.find(
        (edge: WorkflowEdge) =>
          edge.source === nodeId && edge.target === mergeNodeId,
      );
      if (edgeToRemove) {
        edgeChanges.push({
          type: "remove",
          id: edgeToRemove.id,
        });
      }
    });

    const addedNodeIds = Array.from(newNodeIds).filter(
      (id) => !oldNodeIds.has(id),
    );
    addedNodeIds.forEach((nodeId, index) => {
      const entry = newEntries.find((e) => e.nodeId === nodeId);
      if (entry) {
        const newEdge: Edge = {
          id: `${nodeId}-${mergeNodeId}-${index}`,
          source: nodeId,
          target: mergeNodeId,
          sourceHandle: "source",
          targetHandle: `merge-input-${index + 1}`,
          type: "default",
          animated: false,
          data: {
            label: entry.name,
          },
        };

        edgeChanges.push({
          type: "add",
          item: newEdge,
        });
      }
    });

    const keptNodeIds = Array.from(oldNodeIds).filter((id) =>
      newNodeIds.has(id),
    );
    keptNodeIds.forEach((nodeId) => {
      const oldEntry = oldEntries.find((e) => e.nodeId === nodeId);
      const newEntry = newEntries.find((e) => e.nodeId === nodeId);
      const existingEdge = edges.find(
        (edge: WorkflowEdge) =>
          edge.source === nodeId && edge.target === mergeNodeId,
      );

      if (
        oldEntry &&
        newEntry &&
        existingEdge &&
        oldEntry.name !== newEntry.name
      ) {
        edgeChanges.push({
          type: "replace",
          id: existingEdge.id,
          item: {
            ...existingEdge,
            data: {
              ...existingEdge.data,
              label: newEntry.name,
            },
          },
        });
      }
    });

    if (edgeChanges.length > 0) {
      onEdgesChange(edgeChanges);
    }
  }

  static canConnectToMerge(
    nodeId: string,
    _mergeNodeId: string,
    entries: MergeEntry[],
  ): { canConnect: boolean; reason?: string } {
    const store = this.getStore();
    const { nodes, edges } = store;

    const node = nodes.find((n) => n.id === nodeId);
    if (!node) {
      return { canConnect: false, reason: "Nodo no encontrado" };
    }

    if (entries.some((e) => e.nodeId === nodeId)) {
      return { canConnect: false, reason: "El nodo ya está seleccionado" };
    }

    const outgoingEdges = edges.filter(
      (edge: WorkflowEdge) => edge.source === nodeId,
    );
    if (outgoingEdges.length > 0) {
      return {
        canConnect: false,
        reason: "El nodo ya tiene conexiones salientes",
      };
    }

    if (entries.length > 0) {
      const ancestorValidation = CommonAncestorValidator.canAddNodeToMerge(
        nodeId,
        entries,
        nodes,
        edges,
      );

      if (!ancestorValidation.canAdd) {
        return { canConnect: false, reason: ancestorValidation.reason };
      }
    }

    return { canConnect: true };
  }

  static manageOutputVisibility(
    _mergeNodeId: string,
    entries: MergeEntry[],
  ): void {
    const store = this.getStore();
    const { nodes, edges, onNodesChange } = store;

    const validation = CommonAncestorValidator.validateCommonAncestor(
      entries,
      nodes,
      edges as WorkflowEdge[],
    );
    if (!validation.isValid || !validation.commonAncestor) {
      return;
    }

    const commonAncestorId = validation.commonAncestor;
    const intermediateNodeIds = new Set<string>();

    entries.forEach((entry) => {
      if (!entry.nodeId) return;

      let currentId = entry.nodeId;
      while (currentId && currentId !== commonAncestorId) {
        intermediateNodeIds.add(currentId);

        const parentId = CommonAncestorValidator.findDirectParent(
          currentId,
          edges as WorkflowEdge[],
        );
        if (!parentId) break;
        currentId = parentId;
      }
    });

    const nodeChanges = nodes
      .map((node) => {
        const isIntermediate = intermediateNodeIds.has(node.id);
        const currentHideOutputs =
          (node.data as { hide_outputs?: boolean })?.hide_outputs || false;

        if (isIntermediate !== currentHideOutputs) {
          return {
            id: node.id,
            type: "replace" as const,
            item: {
              ...node,
              data: {
                ...node.data,
                hide_outputs: isIntermediate,
              },
            },
          };
        }
        return null;
      })
      .filter(
        (change): change is NonNullable<typeof change> => change !== null,
      );

    if (nodeChanges.length > 0) {
      onNodesChange(nodeChanges);
    }
  }

  static recalculateAllVisibility(): void {
    const store = this.getStore();
    const { nodes, edges, onNodesChange } = store;

    const allIntermediateNodeIds = new Set<string>();

    const mergeNodes = nodes.filter(
      (n) => (n.data as { type?: string })?.type === "merge-node",
    );

    mergeNodes.forEach((mergeNode) => {
      const nodeData = mergeNode.data as {
        inputs?: Array<{
          type: string;
          value: string | { node_id?: string; name?: string };
        }>;
      };
      const entries: MergeEntry[] =
        nodeData.inputs
          ?.filter((input) => input.type === "MERGE_ENTRY")
          .map((input) => {
            const entryValue =
              typeof input.value === "string"
                ? JSON.parse(input.value)
                : input.value;
            return {
              nodeId: entryValue?.node_id,
              name: entryValue?.name,
              type: "MERGE_ENTRY",
            };
          }) || [];

      if (entries.length < 2) return;

      const validation = CommonAncestorValidator.validateCommonAncestor(
        entries,
        nodes,
        edges as WorkflowEdge[],
      );
      if (!validation.isValid || !validation.commonAncestor) return;

      const commonAncestorId = validation.commonAncestor;

      entries.forEach((entry) => {
        if (!entry.nodeId) return;
        let currentId = entry.nodeId;
        while (currentId && currentId !== commonAncestorId) {
          allIntermediateNodeIds.add(currentId);
          const parentId = CommonAncestorValidator.findDirectParent(
            currentId,
            edges as WorkflowEdge[],
          );
          if (!parentId) break;
          currentId = parentId;
        }
      });
    });

    const nodeChanges = nodes
      .map((node) => {
        const shouldBeHidden = allIntermediateNodeIds.has(node.id);
        const isCurrentlyHidden =
          (node.data as { hide_outputs?: boolean })?.hide_outputs || false;

        if (shouldBeHidden !== isCurrentlyHidden) {
          return {
            id: node.id,
            type: "replace" as const,
            item: {
              ...node,
              data: {
                ...node.data,
                hide_outputs: shouldBeHidden,
              },
            },
          };
        }
        return null;
      })
      .filter(
        (change): change is NonNullable<typeof change> => change !== null,
      );

    if (nodeChanges.length > 0) {
      onNodesChange(nodeChanges);
    }
  }

  static getAffectedNodesForVisibility(
    entries: MergeEntry[],
    nodes: Node[],
    _edges: Edge[],
  ): { visibleNodes: string[]; hiddenNodes: string[] } {
    const visibleNodes = new Set<string>();
    const hiddenNodes = new Set<string>();

    entries.forEach((entry) => {
      const branchNodes = this.getNodesInBranch(entry.nodeId, nodes, _edges);

      let foundSelectedNode = false;
      branchNodes.forEach((nodeId) => {
        if (nodeId === entry.nodeId) {
          foundSelectedNode = true;
          visibleNodes.add(nodeId);
        } else if (foundSelectedNode) {
          visibleNodes.add(nodeId);
        } else {
          hiddenNodes.add(nodeId);
        }
      });
    });

    return {
      visibleNodes: Array.from(visibleNodes),
      hiddenNodes: Array.from(hiddenNodes),
    };
  }

  private static getNodesInBranch(
    startNodeId: string,
    nodes: Node[],
    edges: WorkflowEdge[],
  ): string[] {
    const branchNodes: string[] = [];

    const parentNode = CommonAncestorValidator.findDirectParent(
      startNodeId,
      edges,
    );
    if (!parentNode) return branchNodes;

    nodes.forEach((node) => {
      const nodeParent = CommonAncestorValidator.findDirectParent(
        node.id,
        edges,
      );
      if (nodeParent === parentNode) {
        branchNodes.push(node.id);
      }
    });

    branchNodes.sort((a, b) => {
      const nodeA = nodes.find((n) => n.id === a);
      const nodeB = nodes.find((n) => n.id === b);

      if (!nodeA || !nodeB) return 0;

      if (nodeA.position.y !== nodeB.position.y) {
        return nodeA.position.y - nodeB.position.y;
      }

      return nodeA.position.x - nodeB.position.x;
    });

    return branchNodes;
  }
}
